import { Box, CircleUser, User, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Link } from './Link';

export type EntityKind = 'user' | 'group' | 'system' | 'component' | 'unknown';

const kindIcon: Record<EntityKind, LucideIcon> = {
  user: User,
  group: Users,
  system: Box,
  component: Box,
  unknown: CircleUser,
};

/** Parse "group:default/platform-team" → { kind, name }. Bare strings → group. */
export function parseEntityRef(
  ref: string,
  fallbackKind: EntityKind = 'group',
) {
  let kind: string = fallbackKind;
  let name = ref;
  const colon = ref.indexOf(':');
  if (colon >= 0) {
    kind = ref.slice(0, colon).toLowerCase();
    name = ref.slice(colon + 1);
  }
  const slash = name.lastIndexOf('/');
  if (slash >= 0) name = name.slice(slash + 1);
  const known: EntityKind[] = ['user', 'group', 'system', 'component'];
  return {
    kind: (known.includes(kind as EntityKind) ? kind : 'unknown') as EntityKind,
    name,
  };
}

/**
 * Catalog page for an entity ref — `group:default/devops` →
 * `/catalog/default/group/devops`. Bare names (no `kind:`) resolve against
 * `fallbackKind` in the `default` namespace, so an owner string like
 * `platform-team` still links somewhere sensible.
 */
export function entityHref(ref: string, fallbackKind: EntityKind = 'group') {
  const colon = ref.indexOf(':');
  const kind = colon >= 0 ? ref.slice(0, colon).toLowerCase() : fallbackKind;
  const rest = colon >= 0 ? ref.slice(colon + 1) : ref;
  const slash = rest.lastIndexOf('/');
  const namespace = slash >= 0 ? rest.slice(0, slash) : 'default';
  const name = slash >= 0 ? rest.slice(slash + 1) : rest;
  return `/catalog/${namespace}/${kind}/${name}`;
}

export interface EntityRefProps {
  /** An entity ref ("group:default/platform-team") or a bare owner/group name. */
  entityRef: string;
  /** Override the inferred kind (icon). */
  kind?: EntityKind;
  /** Render as a link to the entity. */
  href?: string;
  className?: string;
  /**
   * Wraps a long name onto multiple lines instead of truncating it with an
   * ellipsis — for contexts (e.g. a table's flexible name column) where the
   * full name must always stay visible. Defaults to false (truncate), so
   * every existing caller is unaffected.
   */
  wrap?: boolean;
}

/**
 * Entity reference — an owner / group / system / user shown with its
 * kind icon and readable name. The one way to render references across the
 * app (catalog, approvals, about cards) instead of plain text.
 */
export const EntityRef = ({
  entityRef,
  kind,
  href,
  className,
  wrap = false,
}: EntityRefProps) => {
  const parsed = parseEntityRef(entityRef);
  const resolvedKind = kind ?? parsed.kind;
  const body = (
    <>
      <span
        // In wrap mode the row aligns to the top (items-start), so give the
        // icon a box the height of one text line (text-sm = 20px = h-5) and
        // center it there — it lands on the FIRST line's center whether the
        // name is a single line or wraps, with no magic pixel offset.
        className={cx('flex shrink-0 items-center text-fg-muted', wrap && 'h-5')}
      >
        <Icon as={kindIcon[resolvedKind]} size="xs" />
      </span>
      <span className={wrap ? 'whitespace-normal break-words' : 'truncate'}>
        {parsed.name}
      </span>
    </>
  );
  if (href) {
    return (
      <Link
        href={href}
        variant="quiet"
        // `max-w-full` (not `w-full`) caps the link at its column's width
        // without stretching it to fill the cell — the clickable area stays
        // sized to the (possibly wrapped) name, not the whole cell.
        className={cx(
          'gap-1.5',
          wrap && 'max-w-full items-start',
          className,
        )}
      >
        {body}
      </Link>
    );
  }
  return (
    <span
      className={cx(
        'inline-flex min-w-0 items-center gap-1.5 text-sm text-fg-secondary',
        wrap && 'max-w-full items-start',
        className,
      )}
    >
      {body}
    </span>
  );
};
